import { useCallback, useEffect, useState } from 'react'
import {
  getAllConnectionStatuses, 
  refreshConnectionStatus, 
  ConnectionStatus, 
  filterConnectionsByType 
} from 'api/partners/connectionStatus'
import { PartnerResource } from 'api/models'
import { Toast } from 'components'

export interface UseConnectionStatusesReturn {
  posConnections: ConnectionStatus[]
  toosConnections: ConnectionStatus[]
  isLoading: boolean
  isRefreshing: string | null
  refreshStatus: (connectionStatus: ConnectionStatus) => Promise<void>
  refetchAll: () => Promise<void>
}

const useConnectionStatuses = (partner: PartnerResource): UseConnectionStatusesReturn => {
  const [allConnections, setAllConnections] = useState<ConnectionStatus[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState<string | null>(null)

  const fetchAllConnectionStatuses = useCallback(async () => {
    if (!partner?.id) {
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      const response = await getAllConnectionStatuses(partner.id.toString())
      
      // Attach the included data to each connection for easier access in columns
      const connectionsWithIncluded = response.data.map(connection => ({
        ...connection,
        included: response.included
      }))
      
      setAllConnections(connectionsWithIncluded)
    } catch (error) {
      console.error('Error fetching connection statuses:', error)
      setAllConnections([])
    } finally {
      setIsLoading(false)
    }
  }, [partner?.id])

  useEffect(() => {
    fetchAllConnectionStatuses()
  }, [fetchAllConnectionStatuses])

  const refreshStatus = useCallback(async (connectionStatus: ConnectionStatus) => {
    if (!partner?.id || isRefreshing) return

    setIsRefreshing(connectionStatus.id)

    try {
      // Use the appropriate include parameter based on connection type
      const includeParam = connectionStatus.attributes.connection_type === 'point_of_sale' 
        ? 'point_of_sale' 
        : 'order_platform'
      
      const refreshResponse = await refreshConnectionStatus(
        partner.id.toString(), 
        connectionStatus.id,
        includeParam
      )
      
      // Update the specific connection with the refreshed data
      setAllConnections(prev => 
        prev.map(conn => 
          conn.id === connectionStatus.id 
            ? { ...refreshResponse.data, included: refreshResponse.included }
            : conn
        )
      )
      
      const connectionType = connectionStatus.attributes.connection_type === 'point_of_sale' ? 'POS' : 'TOOS'
      Toast.success({ 
        message: `${connectionType} status refresh requested successfully` 
      }, { autoClose: 3000 })
    } catch (error) {
      console.error('Error refreshing connection status:', error)
      const connectionType = connectionStatus.attributes.connection_type === 'point_of_sale' ? 'POS' : 'TOOS'
      Toast.error({
        message: `Failed to refresh ${connectionType} status. Please try again or contact support.`,
      })
    } finally {
      setIsRefreshing(null)
    }
  }, [partner?.id, isRefreshing, fetchAllConnectionStatuses])

  // Filter connections by type
  const posConnections = filterConnectionsByType(allConnections, 'point_of_sale')
  const toosConnections = filterConnectionsByType(allConnections, 'order_platform')

  return {
    posConnections,
    toosConnections,
    isLoading,
    isRefreshing,
    refreshStatus,
    refetchAll: fetchAllConnectionStatuses,
  }
}

export default useConnectionStatuses 